// Formulario de la orden (paso 2 del alta y edición): validación, payload para save_order y copia
// desde una orden definitiva. El cliente se elige en el paso 1 y viaja como client_id.
import { parseDisplayDate, formatIsoDate } from './dates.ts';
import { parseFolio } from './folio.ts';
import type { WorkType } from './orders.ts';

export interface OrderFormValues {
  folio: string;
  /** Solo al crear: la OS será la OT interna, que el servidor genera al crear la orden (folio_from_ot). */
  folioFromOt: boolean;
  requestDate: string; // dd/mm/aaaa tal como se ve
  equipment: string;
  brand: string;
  model: string;
  reportedFault: string;
  serial: string;
  workType: WorkType | '';
  assignedTo: string; // '' = Sin asignar
}

export type OrderFormErrors = Partial<Record<keyof OrderFormValues, string>>;

export const EMPTY_ORDER_FORM: OrderFormValues = {
  folio: '',
  folioFromOt: false,
  requestDate: '',
  equipment: '',
  brand: '',
  model: '',
  reportedFault: '',
  serial: '',
  workType: '',
  assignedTo: '',
};

const REQUIRED_TEXT: { key: 'equipment' | 'brand' | 'reportedFault'; label: string; max: number }[] = [
  { key: 'equipment', label: 'el Equipo', max: 120 },
  { key: 'brand', label: 'la Marca', max: 120 },
  { key: 'reportedFault', label: 'la Falla reportada', max: 2000 },
];

export function validateOrderForm(values: OrderFormValues): OrderFormErrors {
  const errors: OrderFormErrors = {};
  const folio = parseFolio(values.folio);
  if (!values.folioFromOt && !folio.ok) errors.folio = folio.error;
  const date = parseDisplayDate(values.requestDate);
  if (!date.ok) errors.requestDate = date.error;
  for (const { key, label, max } of REQUIRED_TEXT) {
    const value = values[key].trim();
    if (value === '') errors[key] = `Ingresa ${label}.`;
    else if (value.length > max) errors[key] = `Máximo ${max} caracteres.`;
  }
  if (values.workType === '') errors.workType = 'Elige el Tipo Garantía.';
  if (values.model.trim().length > 120) errors.model = 'Máximo 120 caracteres.';
  if (values.serial.trim().length > 120) errors.serial = 'Máximo 120 caracteres.';
  return errors;
}

/**
 * Campos de la solicitud para save_order. Requiere un formulario válido. Al crear se incluyen el
 * cliente, el modelo y la serie (opcionales); al editar solo los datos de la solicitud (el modelo y
 * la serie se guardan con el diagnóstico).
 */
export function toOrderFields(values: OrderFormValues, options: { clientId?: string; includeTechnical: boolean }) {
  const folio = parseFolio(values.folio);
  const date = parseDisplayDate(values.requestDate);
  if ((!values.folioFromOt && !folio.ok) || !date.ok) throw new Error('Formulario inválido.');
  if (values.workType === '') throw new Error('Formulario inválido.');
  const fields: Record<string, unknown> = {
    ...(values.folioFromOt ? { folio_from_ot: true } : { folio: folio.ok ? folio.value : '' }),
    request_date: date.iso,
    equipment: values.equipment.trim(),
    brand: values.brand.trim(),
    reported_fault: values.reportedFault.trim(),
    work_type: values.workType,
    assigned_to: values.assignedTo === '' ? null : values.assignedTo,
  };
  if (options.clientId) fields['client_id'] = options.clientId;
  if (options.includeTechnical) {
    fields['model'] = values.model.trim();
    fields['serial'] = values.serial.trim();
  }
  return fields;
}

export interface OrderSource {
  equipment: string;
  brand: string;
  model: string;
  serial: string;
  work_type: WorkType;
  folio: string;
  request_date: string;
  reported_fault: string;
  assigned_to: string | null;
}

/** Copia (§6): conserva equipo, marca, modelo, serie y falla; vacía OS, fecha, tipo y técnico. */
export function copyFromOrder(source: OrderSource): OrderFormValues {
  return {
    ...EMPTY_ORDER_FORM,
    equipment: source.equipment,
    brand: source.brand,
    model: source.model,
    serial: source.serial,
    reportedFault: source.reported_fault,
  };
}

/** Valores del formulario de edición a partir de una orden existente. */
export function formFromOrder(source: OrderSource): OrderFormValues {
  return {
    folio: source.folio,
    folioFromOt: false,
    requestDate: formatIsoDate(source.request_date),
    equipment: source.equipment,
    brand: source.brand,
    model: source.model,
    reportedFault: source.reported_fault,
    serial: source.serial,
    workType: source.work_type,
    assignedTo: source.assigned_to ?? '',
  };
}
